import { Store } from '@ngrx/store';
import {
    combineLatest, first, map, Observable, of, switchMap, tap, withLatestFrom,
} from 'rxjs';

import { addProductsToListUseCase } from '../../../application/use-cases/add-products-to-list.use-case';
import { ProductLoaderOrigin } from '../../../domain/enums/product-loader-origin.enum';
import { ProductPaginationService } from '../../../services/product-pagination.service';
import { RECOMMENDED_TYPES_PRODUCT_DETAIL } from '../../domain/constants/recomended-types.constants';
import { ProductUid, RelatedProductType } from '../../domain/models/related-products.models';
import { transformApiProducts } from '../transformers/general-relateds.transformer';

import { findProductUidOnProductArrayUseCase } from './find-product-uid-on-product-array.use-case';
import { filterProductsWithStockFloren } from './floren.use-case';

import { EcommerceContext } from '@core/domain/models/context.models';
import { ProductPublicModels, ProductsService } from '@features/products/product';
import { ProductsArrayFeaturePublicApi, ProductsArrayPublicFunctions, ProductsArrayPublicModels, ProductsArrayPublicSelectors } from '@features/products/productsArray';
// TO REFACTOR: Referencia a @page no permitida
import { getElementDefaultImageTypes } from '@pages/category/store/category-page.selectors';
import { isAllProductSizesNotBuyableUseCase } from '@shared/product/use-cases/product-detail-product.use-case';

// #region PRIVATE
const getProductIds = (products: ProductUid[]): string => {
    const productIds = products
        .reduce((acc, value) => {
            if (RECOMMENDED_TYPES_PRODUCT_DETAIL.includes(value?.relatedType)) {
                return value.uid ? acc.concat(value.uid) : acc;
            }
            return value.product_id ? acc.concat(value.product_id) : acc;
        }, [])
        .join(',');

    return productIds || undefined;
};

const getProductsByIds = (
    store: Store,
    products: ProductUid[],
): Observable<ProductPublicModels.PublicProduct[]> => {
    const productIds: string[] = getProductIds(products)?.split(',');
    return productIds ? store.select(ProductsArrayPublicSelectors.getProductsByIds(productIds)).pipe(first()) : of([]);
};

const getProductsFromStoreAndFilterApi = (
    products: ProductUid[],
    productsStore: ProductPublicModels.PublicProduct[]
): { productsStore: ProductPublicModels.PublicProduct[]; productsApi: ProductUid[] } => {
    return {
        productsStore,
        productsApi: products.filter((productUid) => !findProductUidOnProductArrayUseCase(productsStore, productUid)),
    };
};

const getApiProductsCall = (
    productsArrayPublicApi: ProductsArrayFeaturePublicApi,
    context: EcommerceContext,
    products: ProductUid[],
    relatedType: RelatedProductType
): Observable<ProductsArrayPublicModels.PublicApiProductsArray> => {
    const productIds: string = getProductIds(products);

    if (!productIds) {
        return of({ products: [] });
    }

    if (RECOMMENDED_TYPES_PRODUCT_DETAIL.includes(relatedType)) {
        return productsArrayPublicApi.getMocaArrayWithoutTransformation$(
            context.store.id,
            context.currentCatalog.id,
            productIds,
            context.currentLanguage.id
        );
    }
    return productsArrayPublicApi.getProductsArrayWithoutTransformation$(
        context.store.id,
        context.currentCatalog.id,
        productIds,
        context.currentLanguage.id
    );
};

const filterBackSoonProducts = (productList: ProductPublicModels.PublicApiProduct[]) => {
    return productList.filter((product) => product.visibilityValue !== 'BACK_SOON');
};

const filterProductsConditional = (relatedsType: string, productList: ProductPublicModels.PublicApiProduct[]) => {
    switch (relatedsType) {
        case 'frecuently_bought_together':
        case 'top_sales':
            return filterBackSoonProducts(productList);
        default:
            return productList;
    }
};

const getApiProductsWithStoreProducts = (
    productsArrayPublicApi: ProductsArrayFeaturePublicApi,
    context: EcommerceContext,
    productsApi: ProductUid[],
    productsStore: ProductPublicModels.PublicProduct[],
    relatedType: RelatedProductType,
): Observable<[ProductPublicModels.PublicApiProduct[], ProductPublicModels.PublicProduct[]]> => {
    const apiProductsCall: Observable<ProductsArrayPublicModels.PublicApiProductsArray> = getApiProductsCall(
        productsArrayPublicApi,
        context,
        productsApi,
        relatedType
    );

    return combineLatest([
        apiProductsCall.pipe(
            map((apiProductList) => filterProductsConditional(relatedType, apiProductList.products)),
            map((apiProducts) => transformApiProducts(apiProducts, productsApi, relatedType)),
        ),
        of(productsStore),
    ]);
};

const setColorIdByRelatedProducts = (
    products: ProductPublicModels.PublicApiProduct[],
    relatedProducts: ProductUid[],
    relatedType: RelatedProductType
): ProductPublicModels.PublicApiProduct[] => {
    return products.map((p) => {
        let colorId = p.colorId;

        const relatedProduct = relatedProducts.find((rp) => {
            if (RECOMMENDED_TYPES_PRODUCT_DETAIL.includes(relatedType)) {
                return p.detail.reference.startsWith(rp.uid);
            }
            return p.id === Number(rp.product_id);
        });

        if (relatedProduct) {
            colorId = relatedProduct.colorId;
        }

        return {
            ...p,
            colorId,
        };
    });
};

const getTransformProductsFromApi = (
    context: EcommerceContext,
    productsApi: ProductPublicModels.PublicApiProduct[],
    productsStore: ProductPublicModels.PublicProduct[],
): ProductPublicModels.PublicProduct[] => {
    const categoryId = undefined;
    const productsArrayContext = ProductsArrayPublicFunctions.transformContextToProductsArrayContext(context);

    const productsFiltered = {
        products: productsApi.filter((p) => {
            // ProductBean (first line conditions) && BundleBean (second and third line conditions)
            return p.detail?.colors?.length > 0 && p.bundleProductSummaries?.length === 0
                || p.bundleProductSummaries?.length === 1 && p.bundleProductSummaries[0].detail?.colors?.length > 0
                && p.bundleProductSummaries[0].bundleProductSummaries?.length === 0;
        }),
    };

    const productsApiResult = ProductsArrayPublicFunctions.transformProductsFromApi(
        productsFiltered,
        categoryId,
        productsArrayContext
    );

    return productsApiResult.concat(productsStore);
};

const filterProductsWithStock = (
    products: ProductPublicModels.PublicProduct[],
    type: string
): ProductPublicModels.PublicProduct[] => {
    if (type === 'floren') {
        return filterProductsWithStockFloren(products);
    }
    const filteredProducts = products.filter((product) => product.colors.filter(
        (color) => {
            const filteredSizes = isAllProductSizesNotBuyableUseCase(color.sizes, ['HIDDEN'], false);
            // eslint-disable-next-line no-secrets/no-secrets
            // isAllProductSizesNotBuyable no devuelve un booleano, por lo que esto siempre será truthy
            return filteredSizes;
        }
    ).length > 0);
    return filteredProducts;
};
// #endregion PRIVATE

// #region PUBLIC
export const getProductsBatchByIdsUseCase = (
    productsArrayPublicApi: ProductsArrayFeaturePublicApi,
    store: Store,
    productsService: ProductsService,
    productPaginationService: ProductPaginationService,
    context: EcommerceContext,
    relatedProducts: ProductUid[],
    relatedType: RelatedProductType,
): Observable<ProductPublicModels.PublicProduct[]> => {
    return getProductsByIds(store, relatedProducts).pipe(
        map((productsStore) => getProductsFromStoreAndFilterApi(relatedProducts, productsStore)),
        switchMap(({ productsStore, productsApi }) => getApiProductsWithStoreProducts(
            productsArrayPublicApi,
            context,
            productsApi,
            productsStore,
            relatedType,
        )),
        withLatestFrom(store.select(getElementDefaultImageTypes)),
        tap(([[productsApi, _productsStore], _mapImageDefault]) => { productsService.setProductsApiLegacy(productsApi); }),
        map(([[productsApi, productsStore]]) => ({
            productsApi: setColorIdByRelatedProducts(productsApi, relatedProducts, relatedType),
            productsStore,
        })),
        map(({ productsApi, productsStore }) => getTransformProductsFromApi(
            context,
            productsApi,
            productsStore,
        )),
        tap((products) => {
            if (products.length > 0) {
                addProductsToListUseCase(store, productPaginationService, products, ProductLoaderOrigin.RELATEDS);
            }
        }),
        map((products) => filterProductsWithStock(products, relatedType)),
    );
};
// #endregion PUBLIC

// #region __TESTS__
export const __tests__ = {
    getProductIds,
    getProductsByIds,
    getProductsFromStoreAndFilterApi,
    getApiProductsWithStoreProducts,
    getApiProductsCall,
    filterProductsConditional,
    setColorIdByRelatedProducts,
    getTransformProductsFromApi,
    filterProductsWithStock,
};
